/*
  m365-preorder-cart.js — the mixed-cart banner (Step 5).

  WHY IT IS DRIVEN BY UNICORN'S EVENTS. The store's cart drawer is the Unicorn app, not
  Dawn's. `#unicorn-cart-block` arrives from the server as an EMPTY div and the app
  builds the drawer into it at runtime, so there is no server-side markup to render a
  banner into and nothing to hook in Liquid. The app gives us what we need instead:

      unicornCartReady        — the drawer has been built
      unicornCartDataChanged  — the cart changed and the drawer was re-rendered
      window.unicornCartData  — the cart payload

  Re-rendering is why this has to run on BOTH events and be idempotent: a banner
  injected once on ready is wiped the next time a quantity changes.

  THE PROPERTIES SHAPE IS NOT WHAT CART.JS GIVES YOU. In `window.unicornCartData` a
  line's properties are an ARRAY OF PAIRS — [['Pre-order', 'Ships by 23 Oct']] — and an
  empty ARRAY when there are none. Shopify's /cart.js returns an OBJECT for the same
  thing. Both shapes are handled, because the fallback path reads /cart.js.

  Dawn's own cart-drawer.liquid is deliberately untouched: it still renders, but no
  shopper ever sees it.
*/

(function () {
  'use strict';

  var PROPERTY = 'Pre-order';
  var BANNER_CLASS = 'm365-cart-banner';
  var MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  var MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function propsOf(item) {
    // Array-of-pairs (unicornCartData) or object (/cart.js).
    var p = item && item.properties;
    if (!p) return {};
    if (Array.isArray(p)) {
      var out = {};
      p.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length >= 2) out[pair[0]] = pair[1];
      });
      return out;
    }
    return p;
  }

  /*
    The property carries display text ("Ships by 23 Oct"), not an ISO date, because it
    is written for the customer to read in the cart and on the order. To find the LATEST
    across several pre-order lines we parse it back and project it forward: a day/month
    with no year is read as the next occurrence, which is the right reading for a
    ships-by date. "Ships soon" (a date already past) has no date to rank and sorts last
    deliberately — a known date should win over an unknown one.
  */
  function isoOf(item) {
    var v = propsOf(item)['_preorder_ships_on'];
    if (!v) return null;
    var m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  }

  function dateFromText(text) {
    if (!text) return null;
    var m = String(text).match(/(\d{1,2})\s+([A-Za-z]{3})/);
    if (!m) return null;
    var day = parseInt(m[1], 10);
    var month = MONTHS.indexOf(m[2].toLowerCase().slice(0, 3));
    if (month < 0) return null;
    var now = new Date();
    var d = new Date(now.getFullYear(), month, day);
    // More than a month behind us means it belongs to next year.
    if (d.getTime() < now.getTime() - 31 * 864e5) d.setFullYear(d.getFullYear() + 1);
    return d;
  }

  function summarise(items) {
    var preorder = 0, plain = 0, latest = null;
    (items || []).forEach(function (item) {
      var value = propsOf(item)[PROPERTY];
      if (value) {
        preorder += 1;
        // Prefer the _-prefixed ISO twin; fall back to parsing the wording for any
        // line added before that property existed.
        var d = isoOf(item) || dateFromText(value);
        if (d && (!latest || d > latest)) latest = d;
      } else {
        plain += 1;
      }
    });
    return { mixed: preorder > 0 && plain > 0, latest: latest };
  }

  // Liquid's %-d %b, done in JS: day with no leading zero, three-letter month.
  function formatShips(d) {
    if (!d) return null;
    return d.getDate() + ' ' + MONTH_LABELS[d.getMonth()];
  }

  function message(latest) {
    var when = formatShips(latest);
    if (!when) {
      // Every pre-order line is already past its date, so there is no honest date to
      // print. Say the true thing rather than leave a gap in the sentence.
      return "Your order includes pre-order items, so we'll send everything together "
           + "once they land.";
    }
    return "Your order includes pre-order items, so we'll send everything together by "
         + when + ".";
  }

  function removeBanners(scope) {
    (scope || document).querySelectorAll('.' + BANNER_CLASS).forEach(function (el) {
      el.remove();
    });
  }

  function buildBanner(text) {
    var el = document.createElement('div');
    el.className = BANNER_CLASS;
    el.setAttribute('role', 'status');
    el.textContent = text;
    return el;
  }

  /*
    WHERE THE BANNER GOES, and why it is matched this way.

    The drawer is NOT a shadow root — it is a <dialog> in light DOM inside
    #unicorn-cart-block, so injection is possible. Its class names come in two flavours:
    CSS-module hashes that will change on the app's next release
    (cart_footer__Yi6K0, totals_amounts__qcp5T) and stable unprefixed ones
    (unicorn_cart_footer, unicorn_cart_totals, unicorn_cart_block). We match only on
    the stable ones plus the word "Subtotal", never on a hash.

    Structure, confirmed live:
      #unicorn-cart-block
        dialog
          ...
          .unicorn_cart_footer
            .unicorn_cart_block
              .unicorn_cart_totals
                [row]  "You Saved  -$20.00"
                [row]  "Subtotal   $206.98"     <- banner goes immediately above this

    If no anchor is found the banner is NOT rendered. The previous version appended to
    the block as a fallback, which put it outside the <dialog> entirely — it painted at
    the top-left of the page, over the announcement bar and behind the drawer overlay.
    A banner in the wrong place is worse than none: it is noise the shopper cannot
    connect to anything.
  */
  function subtotalRow(scope) {
    var totals = scope.querySelector('.unicorn_cart_totals');
    if (!totals) return null;
    // The row is a direct child of the totals container whose text names the subtotal.
    var rows = totals.children;
    for (var i = 0; i < rows.length; i++) {
      if (/subtotal/i.test(rows[i].textContent || '')) return rows[i];
    }
    return null;
  }

  function insertInto(scope, text) {
    if (!scope) return false;
    removeBanners(scope);
    var row = subtotalRow(scope);
    if (!row || !row.parentNode) return false;   // no anchor -> render nothing
    row.parentNode.insertBefore(buildBanner(text), row);
    return true;
  }

  /*
    Dawn's own cart page is a different document with different markup, so it gets its
    own anchor: the subtotal block Dawn ships. Same rule — found or nothing.
  */
  function insertIntoCartPage(text) {
    var totals = document.querySelector('.cart__footer .totals, .cart__footer');
    if (!totals || !totals.parentNode) return false;
    removeBanners(document.querySelector('.cart__footer'));
    totals.parentNode.insertBefore(buildBanner(text), totals);
    return true;
  }

  function render(items) {
    var state = summarise(items);
    var drawer = document.getElementById('unicorn-cart-block');

    if (!state.mixed) {
      removeBanners(document);
      return;
    }
    var text = message(state.latest);
    if (drawer) insertInto(drawer, text);
    if (document.querySelector('.cart__footer')) insertIntoCartPage(text);
  }

  function itemsFromUnicorn() {
    var d = window.unicornCartData;
    if (!d) return null;
    if (Array.isArray(d.items)) return d.items;
    if (d.cart && Array.isArray(d.cart.items)) return d.cart.items;
    return null;
  }

  function refresh() {
    var items = itemsFromUnicorn();
    if (items) {
      render(items);
      return;
    }
    // The drawer may not have initialised yet (or we are on the cart page, where the
    // app does not run). Ask Shopify directly rather than guessing.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart.items); })
      .catch(function () { /* no cart, nothing to say */ });
  }

  ['unicornCartReady', 'unicornCartDataChanged', 'unicornItemAddedToCart',
   'unicornCartOpened'].forEach(function (name) {
    document.addEventListener(name, refresh);
    window.addEventListener(name, refresh);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
